import {
  AnswersOptions,
  isClientInsuranceOwner,
  isClientSecondInsuranceOwner,
  isClientThirdInsuranceOwner,
} from "../sections/billing-information-answers";

import { Answers } from ".";

export type InsuranceTierName = "primary" | "secondary" | "tertiary";

export interface SubscriberAnswerFields {
  firstName: AnswersOptions;
  lastName: AnswersOptions;
  middleName: AnswersOptions;
  dateOfBirth: AnswersOptions;
  subscriberId: AnswersOptions;
  streetAddress: AnswersOptions;
  city: AnswersOptions;
  state: AnswersOptions;
  zipCode: AnswersOptions;
  phone: AnswersOptions;
  phoneType: AnswersOptions;
}

export interface CoverageAnswerFields {
  insuranceId: AnswersOptions;
  groupNumber: AnswersOptions;
  subscriberRelationship: AnswersOptions;
}

export interface InsuranceTierConfig {
  name: InsuranceTierName;
  isOwner: (answers: Answers) => boolean;
  order: 1 | 2 | 3;
  /** linkId for this tier's "do you have [Nth] insurance?" question. */
  hasInsuranceAnswer: AnswersOptions;
  /** linkId for this tier's insurance company / payor answer. */
  companyAnswer: AnswersOptions;
  subscriberFields: SubscriberAnswerFields;
  coverageFields: CoverageAnswerFields;
}

/**
 * Shared primary, secondary, and tertiary insurance-tier configs for cleanup and Coverage builders.
 */
export const INSURANCE_TIERS: Record<InsuranceTierName, InsuranceTierConfig> = {
  primary: {
    name: "primary",
    isOwner: isClientInsuranceOwner,
    order: 1,
    hasInsuranceAnswer: AnswersOptions.BillingInformationHealthInsurance,
    companyAnswer: AnswersOptions.BillingInformationInsuranceCompany,
    subscriberFields: {
      firstName: AnswersOptions.BillingInformationSubscriberFirstName,
      lastName: AnswersOptions.BillingInformationSubscriberLastName,
      middleName: AnswersOptions.BillingInformationSubscriberMiddleName,
      dateOfBirth: AnswersOptions.BillingInformationSubscriberDateOfBirth,
      subscriberId: AnswersOptions.BillingInformationSubscriberID,
      streetAddress: AnswersOptions.BillingInformationSubscriberStreetAddress,
      city: AnswersOptions.BillingInformationSubscriberCity,
      state: AnswersOptions.BillingInformationSubscriberState,
      zipCode: AnswersOptions.BillingInformationSubscriberZipCode,
      phone: AnswersOptions.BillingInformationPhone,
      phoneType: AnswersOptions.BillingInformationType,
    },
    coverageFields: {
      insuranceId: AnswersOptions.BillingInformationInsuranceId,
      groupNumber: AnswersOptions.BillingInformationInsuranceGroupNumber,
      subscriberRelationship: AnswersOptions.BillingInformationSubscriberRelationship,
    },
  },
  secondary: {
    name: "secondary",
    isOwner: isClientSecondInsuranceOwner,
    order: 2,
    hasInsuranceAnswer: AnswersOptions.BillingInformationSecondaryHealthInsurance,
    companyAnswer: AnswersOptions.BillingInformationSecondInsuranceCompany,
    subscriberFields: {
      firstName: AnswersOptions.BillingInformationSecondInsuranceSubscriberFirstName,
      lastName: AnswersOptions.BillingInformationSecondInsuranceSubscriberLastName,
      middleName: AnswersOptions.BillingInformationSecondInsuranceSubscriberMiddleName,
      dateOfBirth: AnswersOptions.BillingInformationSecondInsuranceSubscriberDateOfBirth,
      subscriberId: AnswersOptions.BillingInformationSecondInsuranceSubscriberID,
      streetAddress: AnswersOptions.BillingInformationSecondInsuranceSubscriberStreetAddress,
      city: AnswersOptions.BillingInformationSecondInsuranceSubscriberCity,
      state: AnswersOptions.BillingInformationSecondInsuranceSubscriberState,
      zipCode: AnswersOptions.BillingInformationSecondInsuranceSubscriberZipCode,
      phone: AnswersOptions.BillingInformationSecondInsurancePhone,
      phoneType: AnswersOptions.BillingInformationSecondInsuranceType,
    },
    coverageFields: {
      insuranceId: AnswersOptions.BillingInformationSecondInsuranceId,
      groupNumber: AnswersOptions.BillingInformationSecondInsuranceGroupNumber,
      subscriberRelationship:
        AnswersOptions.BillingInformationSecondInsuranceSubscriberRelationship,
    },
  },
  tertiary: {
    name: "tertiary",
    isOwner: isClientThirdInsuranceOwner,
    order: 3,
    hasInsuranceAnswer: AnswersOptions.BillingInformationThirdHealthInsurance,
    companyAnswer: AnswersOptions.BillingInformationThirdInsuranceCompany,
    subscriberFields: {
      firstName: AnswersOptions.BillingInformationThirdInsuranceSubscriberFirstName,
      lastName: AnswersOptions.BillingInformationThirdInsuranceSubscriberLastName,
      middleName: AnswersOptions.BillingInformationThirdInsuranceSubscriberMiddleName,
      dateOfBirth: AnswersOptions.BillingInformationThirdInsuranceSubscriberDateOfBirth,
      subscriberId: AnswersOptions.BillingInformationThirdInsuranceSubscriberID,
      streetAddress: AnswersOptions.BillingInformationThirdInsuranceSubscriberStreetAddress,
      city: AnswersOptions.BillingInformationThirdInsuranceSubscriberCity,
      state: AnswersOptions.BillingInformationThirdInsuranceSubscriberState,
      zipCode: AnswersOptions.BillingInformationThirdInsuranceSubscriberZipCode,
      phone: AnswersOptions.BillingInformationThirdInsurancePhone,
      phoneType: AnswersOptions.BillingInformationThirdInsuranceType,
    },
    coverageFields: {
      insuranceId: AnswersOptions.BillingInformationThirdInsuranceId,
      groupNumber: AnswersOptions.BillingInformationThirdInsuranceGroupNumber,
      subscriberRelationship: AnswersOptions.BillingInformationThirdInsuranceSubscriberRelationship,
    },
  },
};

/** Stable iteration order for all insurance tiers (primary → tertiary). */
export const INSURANCE_TIER_ORDER: InsuranceTierName[] = ["primary", "secondary", "tertiary"];

export const PRIMARY_INSURANCE_TIER = INSURANCE_TIERS.primary;
export const SECONDARY_INSURANCE_TIER = INSURANCE_TIERS.secondary;
export const TERTIARY_INSURANCE_TIER = INSURANCE_TIERS.tertiary;
